/**
 * Transaction process graph for product orders:
 *   - default-purchase
 */
/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes an order for a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Provider or opeartor can mark the product shipped/delivered
  MARK_DELIVERED: 'transition/mark-delivered',
  PROVIDER_MARK_DELIVERED: 'transition/provider-mark-delivered',
  PROVIDER_MARK_RECEIVED: 'transition/provider-mark-received',

  OPERATOR_MARK_DELIVERED: 'transition/operator-mark-delivered',

  // Customer can mark the product received (e.g. picked up from provider)
  // MARK_RECEIVED_FROM_PURCHASED: 'transition/mark-received-from-purchased',

  // Automatic cancellation happens if none marks the delivery happened
  AUTO_CANCEL: 'transition/auto-cancel',

  // Operator can cancel the purchase before product has been marked as delivered / received
  CANCEL: 'transition/cancel',

  // If provider has marked the product delivered (e.g. shipped),
  // customer can then mark the product received
  MARK_RECEIVED: 'transition/mark-received',

  // If customer doesn't mark the product received manually, it can happen automatically
  AUTO_MARK_RECEIVED: 'transition/auto-mark-received',

  // When provider has marked the product delivered, customer or operator can dispute the transaction
  DISPUTE: 'transition/dispute',
  OPERATOR_DISPUTE: 'transition/operator-dispute',

  // If nothing is done to disputed transaction it ends up to Canceled state
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',

  // Operator can cancel disputed transaction manually
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',

  // Operator can mark the disputed transaction as received
  MARK_RECEIVED_FROM_DISPUTED: 'transition/mark-received-from-disputed',
  PROVIDER_MARK_RECEIVED_FROM_DISPUTED: 'transition/provider-mark-received-from-disputed',
  PROVIDER_MARK_CANCEL_FROM_DISPUTED: 'transition/provider-mark-cancel-from-disputed',

  // System moves transaction automatically from received state to complete state
  // This makes it possible to to add notifications to that single transition.
  AUTO_COMPLETE: 'transition/auto-complete',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',

  CHILD_REQUEST_PAYMENT: 'transition/child-request-payment',
  PRE_AUTH_PAYMENT: 'transition/pre-auth-payment',
  UPDATE_CHILD_TRANSCTIONS: 'transition/update-child-transactions',
  CHILD_CONFIRM_PAYMENT: 'transition/child-confirm-payment',
  CHILD_EXPIRE_PAYMENT: 'transition/child-expire-payment',
  CHILD_MARK_DELIVERED: 'transition/child-mark-delivered',
  CHILD_OPERATOR_MARK_DELIVERED: 'transition/child-operator-mark-delivered',
  // CHILD_MARK_RECEIVED_FROM_PURCHASED: 'transition/child-mark-received-from-purchased',
  CHILD_MARK_RECEIVED: 'transition/child-mark-received',
  CHILD_AUTO_MARK_RECEIVED: 'transition/child-auto-mark-received',
  CHILD_DISPUTE: 'transition/child-dispute',
  CHILD_OPERATOR_DISPUTE: 'transition/child-operator-dispute',
  CHILD_CANCEL: 'transition/child-cancel',
  CHILD_AUTO_CANCEL: 'transition/child-auto-cancel',
  CHILD_AUTO_COMPLETE: 'transition/child-auto-complete',
  CHILD_AUTO_CANCEL_FROM_DISPUTED: 'transition/child-auto-cancel-from-disputed',
  CHILD_CANCEL_FROM_DISPUTED: 'transition/child-cancel-from-disputed',
  CHILD_MARK_RECEIVED_FROM_DISPUTED: 'transition/child-mark-received-from-disputed',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PURCHASED: 'purchased',
  DELIVERED: 'delivered',
  RECEIVED: 'received',
  DISPUTED: 'disputed',
  CANCELED: 'canceled',
  COMPLETED: 'completed',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
  PREAUTHORIZED: 'preauthorized',
  UPDATE_CHILD_TRANSACTIONS: 'updated-child-transactions',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-purchase/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.CHILD_REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CHILD_EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CHILD_CONFIRM_PAYMENT]: states.PURCHASED,
        [transitions.PRE_AUTH_PAYMENT]: states.PREAUTHORIZED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.UPDATE_CHILD_TRANSCTIONS]: states.UPDATE_CHILD_TRANSACTIONS,
      },
    },
    [states.UPDATE_CHILD_TRANSACTIONS]: {
      on: { [transitions.CONFIRM_PAYMENT]: states.PURCHASED },
    },
    [states.PURCHASED]: {
      on: {
        [transitions.MARK_DELIVERED]: states.DELIVERED,
        [transitions.PROVIDER_MARK_DELIVERED]: states.DELIVERED,
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        [transitions.AUTO_CANCEL]: states.CANCELED,
        [transitions.CANCEL]: states.CANCELED,
        [transitions.CHILD_OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        [transitions.CHILD_CANCEL]: states.CANCELED,
        [transitions.CHILD_AUTO_CANCEL]: states.CANCELED,
        [transitions.CHILD_MARK_DELIVERED]: states.DELIVERED,
      },
    },

    [states.CANCELED]: {},

    [states.DELIVERED]: {
      on: {
        [transitions.PROVIDER_MARK_RECEIVED]: states.RECEIVED,
        [transitions.MARK_RECEIVED]: states.RECEIVED,
        [transitions.AUTO_MARK_RECEIVED]: states.RECEIVED,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
        [transitions.CHILD_MARK_RECEIVED]: states.RECEIVED,
        [transitions.CHILD_AUTO_MARK_RECEIVED]: states.RECEIVED,
        [transitions.CHILD_DISPUTE]: states.DISPUTED,
        [transitions.CHILD_OPERATOR_DISPUTE]: states.DISPUTED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED]: states.COMPLETED,
        [transitions.MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,
        [transitions.CHILD_AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.CHILD_CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.PROVIDER_MARK_CANCEL_FROM_DISPUTED]: states.RECEIVED,
        [transitions.PROVIDER_MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,
        [transitions.CHILD_MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,
      },
    },

    [states.RECEIVED]: {
      on: {
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
        [transitions.CHILD_AUTO_COMPLETE]: states.COMPLETED,
      },
    },

    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.CONFIRM_PAYMENT,
    transitions.AUTO_CANCEL,
    transitions.CANCEL,
    transitions.MARK_DELIVERED,
    transitions.PROVIDER_MARK_DELIVERED,
    transitions.OPERATOR_MARK_DELIVERED,
    transitions.DISPUTE,
    transitions.OPERATOR_DISPUTE,
    transitions.AUTO_COMPLETE,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.CHILD_REQUEST_PAYMENT,
    transitions.PRE_AUTH_PAYMENT,
    transitions.UPDATE_CHILD_TRANSCTIONS,
    transitions.CHILD_CONFIRM_PAYMENT,
    transitions.CHILD_EXPIRE_PAYMENT,
    transitions.CHILD_MARK_DELIVERED,
    transitions.CHILD_OPERATOR_MARK_DELIVERED,
    transitions.CHILD_MARK_RECEIVED,
    transitions.CHILD_AUTO_MARK_RECEIVED,
    transitions.CHILD_DISPUTE,
    transitions.CHILD_OPERATOR_DISPUTE,
    transitions.CHILD_CANCEL,
    transitions.CHILD_AUTO_CANCEL,
    transitions.CHILD_AUTO_COMPLETE,
    transitions.CHILD_AUTO_CANCEL_FROM_DISPUTED,
    transitions.CHILD_CANCEL_FROM_DISPUTED,
    transitions.CHILD_MARK_RECEIVED_FROM_DISPUTED,
    transitions.PROVIDER_MARK_RECEIVED,
    transitions.PROVIDER_MARK_RECEIVED_FROM_DISPUTED,
    transitions.PROVIDER_MARK_CANCEL_FROM_DISPUTED,
  ].includes(transition);
};
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.CHILD_REQUEST_PAYMENT,
  ].includes(transition);
};

// Check when transaction is completed (item is received and review notifications sent)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.PROVIDER_MARK_RECEIVED,
    transitions.PROVIDER_MARK_RECEIVED_FROM_DISPUTED,
    transitions.AUTO_COMPLETE,
    transitions.CHILD_AUTO_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.CHILD_EXPIRE_PAYMENT,
    transitions.CANCEL,
    transitions.CHILD_CANCEL,
    transitions.AUTO_CANCEL,
    transitions.CHILD_AUTO_CANCEL,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CHILD_AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.CHILD_CANCEL_FROM_DISPUTED,
    transitions.PROVIDER_MARK_RECEIVED_FROM_DISPUTED,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PURCHASED];

export const purchaseProcessRequestedTransitions = [
  transitions.REQUEST_PAYMENT,
  transitions.EXPIRE_PAYMENT,
  transitions.PRE_AUTH_PAYMENT,
  transitions.UPDATE_CHILD_TRANSCTIONS,
  transitions.CONFIRM_PAYMENT,
  transitions.MARK_DELIVERED,
  transitions.PROVIDER_MARK_DELIVERED,
  transitions.MARK_RECEIVED,
  transitions.AUTO_MARK_RECEIVED,
  transitions.DISPUTE,
  transitions.OPERATOR_DISPUTE,
  transitions.CANCEL,
  transitions.AUTO_CANCEL,
  transitions.MARK_RECEIVED_FROM_DISPUTED,
  transitions.CANCEL_FROM_DISPUTED,
  transitions.AUTO_CANCEL_FROM_DISPUTED,
  transitions.AUTO_COMPLETE,
  transitions.REVIEW_1_BY_CUSTOMER,
  transitions.REVIEW_1_BY_PROVIDER,
  transitions.REVIEW_2_BY_CUSTOMER,
  transitions.REVIEW_2_BY_PROVIDER,
  transitions.OPERATOR_MARK_DELIVERED,
  transitions.PROVIDER_MARK_RECEIVED,
  transitions.PROVIDER_MARK_RECEIVED_FROM_DISPUTED,
  transitions.PROVIDER_MARK_CANCEL_FROM_DISPUTED,
];
